// pages/MessageBoard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Nav, Button, ListGroup, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getUser } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const CREATOR_ROLES = ['admin', 'masteradmin', 'companyadmin', 'departmentadmin'];

function MessageBoard() {
  const navigate = useNavigate();
  const currentUser = getUser();
  const role = currentUser?.role;
  const isCreator = CREATOR_ROLES.includes(role);

  const [tab, setTab] = useState('all');
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(() => {
    const filterParam = tab === 'unread' ? '?filter=unread' : '';
    axios.get(`${API_BASE_URL}/messages/get_messages.php${filterParam}`, { withCredentials: true })
      .then(response => setMessages(Array.isArray(response.data) ? response.data : []))
      .catch(error => console.error('Error fetching messages:', error));
  }, [tab]);

  const fetchDrafts = useCallback(() => {
    if (!isCreator) return;
    axios.get(`${API_BASE_URL}/messages/get_my_drafts.php`, { withCredentials: true })
      .then(response => setDrafts(Array.isArray(response.data) ? response.data : []))
      .catch(error => console.error('Error fetching drafts:', error));
  }, [isCreator]);

  useEffect(() => {
    setLoading(true);
    if (tab === 'drafts') {
      fetchDrafts();
    } else {
      fetchMessages();
    }
    setLoading(false);
  }, [tab, fetchMessages, fetchDrafts]);

  const handleExpand = (message) => {
    const opening = expandedId !== message.id;
    setExpandedId(opening ? message.id : null);
    if (opening && !message.is_read) {
      axios.post(`${API_BASE_URL}/messages/mark_message_read.php`, { id: message.id }, { withCredentials: true })
        .then(() => setMessages(prev => prev.map(m => m.id === message.id ? { ...m, is_read: true } : m)))
        .catch(error => console.error('Error marking message read:', error));
    }
  };

  const handleSend = (draft) => {
    axios.post(`${API_BASE_URL}/messages/send_message.php`, { id: draft.id }, { withCredentials: true })
      .then(response => {
        if (!response.data.success) throw new Error(response.data.message);
        Swal.fire({ icon: 'success', text: 'Message sent.' });
        fetchDrafts();
      })
      .catch(error => Swal.fire({ icon: 'error', text: error.message || 'Failed to send message.' }));
  };

  // source: 'draft' or 'sent' — deleting is creator-only regardless of
  // status (backend allows both), so a sent message needs its own delete
  // affordance in the main list too, not just the My Drafts tab.
  const handleDelete = async (id, source) => {
    const result = await Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this message?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    axios.post(`${API_BASE_URL}/messages/delete_message.php`, { id }, { withCredentials: true })
      .then(response => {
        if (!response.data.success) throw new Error(response.data.message);
        if (source === 'draft') {
          setDrafts(prev => prev.filter(d => d.id !== id));
        } else {
          setMessages(prev => prev.filter(m => m.id !== id));
        }
      })
      .catch(error => Swal.fire({ icon: 'error', text: error.message || 'Failed to delete message.' }));
  };

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Message Board</h2>
        {isCreator && (
          <Button variant="primary" onClick={() => navigate('/messages/new')}>New Message</Button>
        )}
      </div>

      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="all">All</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="unread">Unread</Nav.Link></Nav.Item>
        {isCreator && <Nav.Item><Nav.Link eventKey="drafts">My Drafts</Nav.Link></Nav.Item>}
      </Nav>

      {loading ? (
        <p>Loading...</p>
      ) : tab === 'drafts' ? (
        <ListGroup>
          {drafts.length > 0 ? drafts.map(draft => (
            <ListGroup.Item key={draft.id}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{draft.title}</strong>
                  <div className="text-muted small">Last updated {new Date(draft.updated_at).toLocaleString()}</div>
                </div>
                <div>
                  <Link to={`/messages/edit/${draft.id}`} className="btn btn-sm btn-secondary me-2">Edit</Link>
                  <Button size="sm" variant="primary" className="me-2" onClick={() => handleSend(draft)}>Send</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(draft.id, 'draft')}>Delete</Button>
                </div>
              </div>
            </ListGroup.Item>
          )) : (
            <ListGroup.Item>No drafts.</ListGroup.Item>
          )}
        </ListGroup>
      ) : (
        <ListGroup>
          {messages.length > 0 ? messages.map(message => (
            <ListGroup.Item key={message.id} action onClick={() => handleExpand(message)} style={{ cursor: 'pointer' }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{message.title}</strong>
                  {!message.is_read && <Badge bg="danger" className="ms-2">Unread</Badge>}
                  <div className="text-muted small">
                    {message.created_by_name} — {new Date(message.sent_at).toLocaleString()}
                  </div>
                  {expandedId === message.id && (
                    <p className="mt-2 mb-0">{message.body}</p>
                  )}
                </div>
                {currentUser && String(message.created_by) === String(currentUser.id) && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={(e) => { e.stopPropagation(); handleDelete(message.id, 'sent'); }}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </ListGroup.Item>
          )) : (
            <ListGroup.Item>No messages.</ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
}

export default MessageBoard;
